/*
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * useLangPrefix — language-aware routing hook
 *
 * The app uses createLocalizeRouter (utils/utils.ts) which prefixes every
 * route with /:lang. Any hardcoded "/en/..." path breaks for non-English
 * users. This hook reads the active :lang route param and returns it with
 * an "en" fallback, along with helpers for building language-prefixed paths.
 *
 * S531: Introduced to fix the systemic hardcoded "/en/" bug found across
 * 40+ navigation calls. Centralising the logic here means a single source
 * of truth — if the routing convention changes, only this file changes.
 *
 * Usage:
 *   const { lang, dashboardPath, checkoutPath } = useLangPrefix();
 *   navigate(dashboardPath("products"));
 */

import { useParams } from "react-router-dom";
import { useCallback } from "react";

export interface LangPrefixResult {
  /** Active language code (e.g. "en", "fr", "de"). Falls back to "en". */
  lang: string;
  /** Build a /lang/dashboard/<section> path. */
  dashboardPath: (section: string) => string;
  /** Build a /lang/<page> path for top-level pages. */
  pagePath: (page: string) => string;
}

export function useLangPrefix(): LangPrefixResult {
  const { lang: routeLang } = useParams<{ lang?: string }>();
  const lang = routeLang || "en";

  const dashboardPath = useCallback(
    (section: string) => `/${lang}/dashboard/${section}`,
    [lang],
  );

  const pagePath = useCallback(
    (page: string) => `/${lang}/${page}`,
    [lang],
  );

  return { lang, dashboardPath, pagePath };
}

/**
 * Pure helper for non-component contexts (navigation utilities, plain functions).
 * Accepts the lang string directly rather than reading from a hook.
 */
export function buildDashboardPath(lang: string, section: string): string {
  return `/${lang}/dashboard/${section}`;
}

export function buildPagePath(lang: string, page: string): string {
  return `/${lang}/${page}`;
}
